import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useTheme,
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    Radio,
    Paper,
    Snackbar,
    Alert,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    LinearProgress,
    Tooltip
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import api from '../../../services/api';

const fieldTypes = [
    { value: 'text', label: 'Texto Curto' },
    { value: 'textarea', label: 'Texto Longo' },
    { value: 'radio', label: 'Múltipla Escolha' },
    { value: 'checkbox', label: 'Caixas de Seleção' },
    { value: 'select', label: 'Lista Suspensa' }
];

const styles = {
    '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' }
    },
    '@keyframes spin': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' }
    },
    fadeIn: {
        animation: 'fadeIn 0.3s ease-out forwards'
    },
    spin: {
        animation: 'spin 1s linear infinite'
    },
    buttonHover: {
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
        },
        '&:active': {
            transform: 'scale(0.95)'
        }
    },
    progressBar: {
        transition: 'width 0.5s ease-in-out'
    }
};

const FormBuilderPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { id } = useParams();
    const [template, setTemplate] = useState(null);
    const [sections, setSections] = useState([]);
    const [initialSections, setInitialSections] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(true);
    const [editingSection, setEditingSection] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [editingOptions, setEditingOptions] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [useSteps, setUseSteps] = useState('no');
    const [desiredStepCount, setDesiredStepCount] = useState(1);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const deepEqual = (obj1, obj2) => {
        if (obj1 === obj2) return true;
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        for (const key of keys1) {
            if (!keys2.includes(key)) return false;
            if (!deepEqual(obj1[key], obj2[key])) return false;
        }
        return true;
    };

    const checkForChanges = () => {
        const hasSectionsChanged = !deepEqual(initialSections, sections);
        setHasChanges(hasSectionsChanged);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!id) {
                setLoading(false);
                showSnackbar('ID do template inválido.', 'error');
                navigate('/city_hall/forms-templates');
                return;
            }

            try {
                setLoading(true);
                const templateResponse = await api.get(`/form-templates/${id}`);
                if (!templateResponse.data || !templateResponse.data.id) {
                    throw new Error('Template não encontrado ou inválido');
                }
                setTemplate(templateResponse.data);

                const sectionsResponse = await api.get(`/form-sections/template/${id}`);
                const sectionsWithFields = await Promise.all(sectionsResponse.data.map(async section => {
                    const fieldsResponse = await api.get(`/form-fields/section/${section.id}`);
                    return {
                        ...section,
                        description: section.description || '',
                        fields: fieldsResponse.data.map(field => ({
                            ...field,
                            description: field.description || ''
                        })) || []
                    };
                }));
                setSections(sectionsWithFields);
                setInitialSections(JSON.parse(JSON.stringify(sectionsWithFields)));
            } catch (error) {
                showSnackbar(`Erro: ${error.message || 'Erro ao carregar template ou seções'}`, 'error');
                navigate('/city_hall/forms-templates');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, navigate]);

    useEffect(() => {
        setDesiredStepCount(Math.max(1, sections.length));
        checkForChanges();
    }, [sections]);

    const showSnackbar = (message, severity) => {
        const suppressedMessages = [
            'Seção adicionada com sucesso',
            'Seção removida com sucesso',
            'Pergunta adicionada com sucesso',
            'Adicionando nova pergunta',
            'Pergunta atualizada com sucesso'
        ];

        if (!suppressedMessages.includes(message)) {
            setSnackbar({ open: true, message, severity });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const ensureArray = (value) => {
        if (Array.isArray(value)) return value;
        if (value === null || value === undefined) return [];
        return [value];
    };

    const handleAddSection = async () => {
        try {
            const newSection = {
                title: 'Nova Seção',
                description: null,
                order: sections.length + 1,
                formTemplateId: id
            };
            const response = await api.post('/form-sections', newSection);
            setSections([...sections, { ...response.data, fields: [] }]);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao adicionar seção';
            showSnackbar(errorMessage, 'error');
            console.error('Error adding section:', error);
        }
    };

    const handleUpdateSection = async (sectionId, updatedData) => {
        try {
            const sectionToUpdate = sections.find(s => s.id === sectionId);
            const description = updatedData.description?.trim();
            const payload = {
                ...updatedData,
                description: description || null,
                formTemplateId: sectionToUpdate.formTemplateId
            };
            await api.put(`/form-sections/${sectionId}`, payload);
            setSections(sections.map(section =>
                section.id === sectionId ? { ...section, ...updatedData, description: description || null } : section
            ));
            setEditingSection(null);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao atualizar seção';
            showSnackbar(errorMessage, 'error');
            console.error('Error updating section:', error);
        }
    };

    const handleDeleteSection = async (sectionId) => {
        try {
            const section = sections.find(s => s.id === sectionId);
            if (section.fields && section.fields.length > 0) {
                await Promise.all(section.fields.map(field =>
                    api.delete(`/form-fields/${field.id}`)
                ));
            }
            await api.delete(`/form-sections/${sectionId}`);
            setSections(sections.filter(section => section.id !== sectionId));
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao remover seção';
            showSnackbar(errorMessage, 'error');
            console.error('Error deleting section:', error);
        }
    };

    const handleAddField = async (sectionId) => {
        try {
            setEditingField(null);
            const newField = {
                label: '',
                description: null,
                type: 'text',
                mandatory: 0,
                options: [],
                order: sections.find(s => s.id === sectionId)?.fields?.length || 0,
                formSectionId: sectionId
            };
            setEditingField(newField);
            setEditingOptions(null);
            setHasChanges(true);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao preparar pergunta';
            showSnackbar(errorMessage, 'error');
            console.error('Error preparing field:', error);
        }
    };

    const handleUpdateField = async (sectionId, fieldId, updatedData) => {
        const { description } = updatedData;
        try {
            const payload = {
                ...updatedData,
                description: description || null,
                formSectionId: sectionId
            };
            const response = await api.put(`/form-fields/${fieldId}`, payload);
            setSections(sections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        fields: section.fields.map(field =>
                            field.id === fieldId ? response.data : field
                        )
                    };
                }
                return section;
            }));
            setEditingField(null);
            setEditingOptions(null);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao atualizar pergunta';
            showSnackbar(errorMessage, 'error');
            console.error('Error updating field:', error);
        }
    };

    const handleSaveNewField = async (sectionId, fieldData) => {
        try {
            const response = await api.post('/form-fields', {
                ...fieldData,
                formSectionId: sectionId,
                order: sections.find(s => s.id === sectionId)?.fields?.length || 0
            });
            setSections(sections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        fields: [...(section.fields || []), response.data]
                    };
                }
                return section;
            }));
            setEditingField(null);
            setEditingOptions(null);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao adicionar pergunta';
            showSnackbar(errorMessage, 'error');
            console.error('Error adding field:', error);
        }
    };

    const handleDeleteField = async (sectionId, fieldId) => {
        try {
            await api.delete(`/form-fields/${fieldId}`);
            setSections(sections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        fields: section.fields.filter(field => field.id !== fieldId)
                    };
                }
                return section;
            }));
            setEditingField(null);
            setEditingOptions(null);
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao remover pergunta';
            showSnackbar(errorMessage, 'error');
            console.error('Error deleting field:', error);
        }
    };

    const handleAddOption = () => {
        if (!editingField) return;
        setEditingOptions({
            ...(editingOptions || {
                fieldId: editingField.id,
                sectionId: editingField.formSectionId,
                options: [...(editingField.options || [])]
            }),
            options: [...(editingOptions?.options || editingField.options || []), '']
        });
        setHasChanges(true);
    };

    const handleCancelEdit = () => {
        setEditingField(null);
        setEditingOptions(null);
        checkForChanges();
    };

    const renderFieldEditor = (field, sectionId) => (
        <Box sx={{ mt: 2, animation: styles.fadeIn, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <TextField
                fullWidth
                label="Pergunta (Obrigatório)"
                value={editingField?.label || ''}
                onChange={(e) => {
                    setEditingField({ ...editingField, label: e.target.value });
                    setHasChanges(true);
                }}
                sx={{
                    mb: 2,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        '&.Mui-focused fieldset': {
                            borderColor: '#10641c',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#10641c',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: '#10641c',
                    },
                    [theme.breakpoints.down('sm')]: { fontSize: '0.9rem', p: 1 },
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ autoComplete: 'off' }}
            />
            <TextField
                fullWidth
                label="Descrição (Opcional)"
                value={editingField?.description || ''}
                onChange={(e) => {
                    setEditingField({ ...editingField, description: e.target.value });
                    setHasChanges(true);
                }}
                sx={{
                    mb: 2,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        '&.Mui-focused fieldset': {
                            borderColor: '#10641c',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#10641c',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: '#10641c',
                    },
                    [theme.breakpoints.down('sm')]: { fontSize: '0.9rem', p: 1 },
                }}
                InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink sx={{ color: '#10641c', '&.Mui-focused': { color: '#10641c' } }}>
                    Tipo de Pergunta
                </InputLabel>
                <Select
                    value={editingField?.type || 'text'}
                    onChange={(e) => {
                        setEditingField({ ...editingField, type: e.target.value });
                        setHasChanges(true);
                    }}
                    label="Tipo de Pergunta"
                    sx={{
                        bgcolor: '#f9f9f9',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                        },
                        '& .MuiSelect-select': {
                            borderRadius: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#10641c',
                        },
                    }}
                >
                    {fieldTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                            {type.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Checkbox
                    checked={!!editingField?.mandatory}
                    onChange={(e) => {
                        setEditingField({ ...editingField, mandatory: Number(e.target.checked) });
                        setHasChanges(true);
                    }}
                    color="primary"
                />
                <Typography variant="body2" fontWeight="medium">Obrigatório</Typography>
            </Box>
            {['radio', 'checkbox', 'select'].includes(editingField?.type) && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Opções:
                    </Typography>
                    {(editingOptions?.options || editingField?.options || []).map((option, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <TextField
                                fullWidth
                                value={option}
                                onChange={(e) => {
                                    const newOptions = [...(editingOptions?.options || editingField?.options || [])];
                                    newOptions[index] = e.target.value;
                                    setEditingOptions({ fieldId: field.id, sectionId, options: newOptions });
                                    setHasChanges(true);
                                }}
                                sx={{
                                    mr: 1,
                                    bgcolor: '#f9f9f9',
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1,
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#10641c',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#10641c',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#10641c',
                                    },
                                    [theme.breakpoints.down('sm')]: { fontSize: '0.9rem', p: 1 },
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ autoComplete: 'off' }}
                            />
                            <Tooltip title="Remover Opção">
                                <IconButton
                                    onClick={() => {
                                        const newOptions = [...(editingOptions?.options || editingField?.options || [])];
                                        newOptions.splice(index, 1);
                                        setEditingOptions({ fieldId: field.id, sectionId, options: newOptions });
                                        setHasChanges(true);
                                    }}
                                    sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddOption}
                        sx={{
                            mt: 1,
                            color: '#10641c',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#e8f5e9' },
                            ...styles.buttonHover
                        }}
                    >
                        Adicionar Opção
                    </Button>
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                    onClick={handleCancelEdit}
                    sx={{
                        color: '#d32f2f',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#ffebee' },
                        ...styles.buttonHover
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (editingField.id) {
                            handleUpdateField(sectionId, field.id, {
                                label: editingField.label,
                                description: editingField.description || '',
                                type: editingField.type,
                                mandatory: editingField.mandatory,
                                options: editingOptions?.options || editingField.options || []
                            });
                        } else {
                            handleSaveNewField(sectionId, {
                                label: editingField.label,
                                description: editingField.description || '',
                                type: editingField.type,
                                mandatory: editingField.mandatory,
                                options: editingOptions?.options || editingField.options || []
                            });
                        }
                    }}
                    sx={{
                        bgcolor: '#10641c',
                        '&:hover': { bgcolor: '#0a3d0f' },
                        borderRadius: 1,
                        textTransform: 'none',
                        ...styles.buttonHover
                    }}
                >
                    Salvar
                </Button>
            </Box>
        </Box>
    );

    const renderFieldPreview = (field, sectionId) => {
        const options = ensureArray(field.options);
        return (
            <Box sx={{ mb: 2, animation: styles.fadeIn, p: 2, bgcolor: '#fafafa', borderRadius: 2, borderLeft: '4px solid #10641c' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                        {field.mandatory ? (
                            <Typography variant="subtitle1" fontWeight="medium">
                                {field.label} {field.mandatory && <span style={{ color: '#d32f2f' }}>*</span>}
                            </Typography>
                        ) : (
                            <Typography variant="subtitle1" fontWeight="medium">
                                {field.label}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            {field.description || 'Nenhuma descrição fornecida'}
                        </Typography>
                    </Box>
                    <Box>
                        <Tooltip title="Editar Pergunta">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setEditingField({ ...field });
                                    setHasChanges(true);
                                }}
                                sx={{ color: '#10641c', '&:hover': { bgcolor: '#e8f5e9' } }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir Pergunta">
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteField(sectionId, field.id)}
                                sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                {(() => {
                    switch (field.type) {
                        case 'text':
                            return (
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Resposta curta"
                                    disabled
                                    sx={{ bgcolor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                />
                            );
                        case 'textarea':
                            return (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    placeholder="Resposta longa"
                                    disabled
                                    sx={{ bgcolor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                />
                            );
                        case 'radio':
                            return (
                                <Box>
                                    {options.map((option, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Radio disabled color="primary" />
                                            <Typography>{option}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            );
                        case 'checkbox':
                            return (
                                <Box>
                                    {options.map((option, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Checkbox disabled color="primary" />
                                            <Typography>{option}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            );
                        case 'select':
                            return (
                                <FormControl fullWidth>
                                    <Select
                                        disabled
                                        displayEmpty
                                        sx={{ bgcolor: '#fff', borderRadius: 1 }}
                                    >
                                        <MenuItem value="">
                                            <em>Selecione uma opção</em>
                                        </MenuItem>
                                        {options.map((option, index) => (
                                            <MenuItem key={index} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        default:
                            return null;
                    }
                })()}
            </Box>
        );
    };

    const renderFieldInput = (field, sectionId) => {
        if (editingField?.id === field.id || (!field.id && editingField && editingField.formSectionId === sectionId)) {
            return renderFieldEditor(field, sectionId);
        }
        return renderFieldPreview(field, sectionId);
    };

    const handleSave = async () => {
        try {
            showSnackbar('Formulário salvo com sucesso', 'success');
            setInitialSections(JSON.parse(JSON.stringify(sections)));
            setHasChanges(false);
            navigate('/city_hall/forms-templates');
        } catch (error) {
            const errorMessage = error.response?.data?.details || 'Erro ao salvar formulário';
            showSnackbar(errorMessage, 'error');
            console.error('Error saving form:', error);
        }
    };

    const getSectionsForStep = () => {
        if (useSteps === 'no') {
            return sections;
        }
        const totalSteps = Math.max(1, desiredStepCount);
        const sectionsPerStep = Math.ceil(sections.length / totalSteps);
        const startIndex = (currentStep - 1) * sectionsPerStep;
        const endIndex = startIndex + sectionsPerStep;
        return sections.slice(startIndex, endIndex);
    };

    const getTotalSteps = () => {
        return useSteps === 'no' ? 1 : Math.max(1, desiredStepCount);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#10641c', ...styles.spin }} />
            </Box>
        );
    }

    if (!template || !template.id) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    Template inválido ou não encontrado.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/city_hall/forms-templates')}
                    sx={{ mt: 2, bgcolor: '#10641c', '&:hover': { bgcolor: '#0a3d0f' } }}
                >
                    Voltar para Formulários
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
            bgcolor: '#f5f7fa',
            minHeight: '100vh',
            p: isMobile ? 1 : 2,
            maxWidth: '800px',
            margin: 'auto'
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 0,
                px: 0,
                mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ color: '#10641c' }}
                    >
                        Editar Formulário
                    </Typography>
                </Box>
            </Box>

            {useSteps === 'yes' && sections.length > 0 && (
                <Box sx={{ mb: 2, animation: styles.fadeIn }}>
                    <Typography variant="h6" align="center" fontWeight="bold">
                        Etapa {currentStep} de {getTotalSteps()}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={(currentStep / getTotalSteps()) * 100}
                        sx={{
                            mt: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: '#10641c',
                                ...styles.progressBar
                            }
                        }}
                    />
                </Box>
            )}

            <Paper sx={{
                p: 3,
                mb: 2,
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                borderLeft: '4px solid #10641c',
                bgcolor: '#fff',
                animation: styles.fadeIn
            }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                    {template.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {template.description || 'Sem descrição'}
                </Typography>
            </Paper>

            <Paper sx={{
                p: 3,
                mb: 2,
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                borderLeft: '4px solid #10641c',
                bgcolor: '#fff',
                animation: styles.fadeIn
            }}>
                {getSectionsForStep().map((section, sectionIndex) => {
                    const safeFields = ensureArray(section.fields);
                    return (
                        <Box key={section.id}>
                            {sectionIndex > 0 && (
                                <Box sx={{
                                    borderTop: '2px solid #d0d0d0',
                                    my: 4,
                                    mx: -3,
                                }} />
                            )}

                            <Box sx={{
                                mb: 5,
                                p: 2,
                                bgcolor: '#fafafa',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                            }}>
                                {editingSection?.id === section.id ? (
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Título da Seção"
                                            value={editingSection.title}
                                            onChange={(e) => {
                                                setEditingSection({ ...editingSection, title: e.target.value });
                                                setHasChanges(true);
                                            }}
                                            sx={{
                                                mb: 2,
                                                bgcolor: '#f9f9f9',
                                                borderRadius: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#10641c',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                [theme.breakpoints.down('sm')]: { fontSize: '0.9rem', p: 1 },
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Descrição (opcional)"
                                            value={editingSection.description}
                                            onChange={(e) => {
                                                setEditingSection({ ...editingSection, description: e.target.value });
                                                setHasChanges(true);
                                            }}
                                            sx={{
                                                mb: 2,
                                                bgcolor: '#f9f9f9',
                                                borderRadius: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1,
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#10641c',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                [theme.breakpoints.down('sm')]: { fontSize: '0.9rem', p: 1 },
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                            <Button
                                                onClick={() => setEditingSection(null)}
                                                sx={{
                                                    color: '#d32f2f',
                                                    textTransform: 'none',
                                                    '&:hover': { bgcolor: '#ffebee' },
                                                    ...styles.buttonHover
                                                }}
                                            >
                                                Cancelar
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleUpdateSection(section.id, {
                                                    title: editingSection.title || 'Nova Seção',
                                                    description: editingSection.description || ''
                                                })}
                                                sx={{
                                                    bgcolor: '#10641c',
                                                    '&:hover': { bgcolor: '#0a3d0f' },
                                                    borderRadius: 1,
                                                    textTransform: 'none',
                                                    ...styles.buttonHover
                                                }}
                                            >
                                                Salvar
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold">
                                                    Seção {useSteps === 'yes' ? sections.indexOf(section) + 1 : sectionIndex + 1}: {section.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {section.description || 'Nenhuma descrição fornecida'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Tooltip title="Editar Seção">
                                                    <IconButton
                                                        onClick={() => {
                                                            setEditingSection(section);
                                                            setHasChanges(true);
                                                        }}
                                                        sx={{ color: '#10641c', '&:hover': { bgcolor: '#e8f5e9' } }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir Seção">
                                                    <IconButton
                                                        onClick={() => handleDeleteSection(section.id)}
                                                        sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                {safeFields.map((field) => (
                                    <Box key={field.id} sx={{ mb: 3 }}>
                                        {renderFieldInput(field, section.id)}
                                    </Box>
                                ))}
                                {editingField && !editingField.id && editingField.formSectionId === section.id && (
                                    <Box sx={{ mb: 3 }}>
                                        {renderFieldEditor(editingField, section.id)}
                                    </Box>
                                )}
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddField(section.id)}
                                    sx={{
                                        mt: 1,
                                        color: '#10641c',
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#e8f5e9' },
                                        ...styles.buttonHover
                                    }}
                                >
                                    Adicionar Pergunta
                                </Button>
                            </Box>
                        </Box>
                    );
                })}

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSection}
                    sx={{
                        bgcolor: '#10641c',
                        '&:hover': { bgcolor: '#0a3d0f' },
                        borderRadius: 1,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        ...styles.buttonHover
                    }}
                    disabled={!template?.id}
                >
                    Adicionar Seção
                </Button>
            </Paper>

            <Box sx={{
                mt: 2,
                px: 2
            }}>
                <Box sx={{ borderTop: '1px solid #e0e0e0', mb: 2 }} />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                }}>
                    {useSteps === 'yes' && sections.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'nowrap',
                        }}>
                            <Button
                                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                                disabled={currentStep === 1}
                                sx={{
                                    color: '#10641c',
                                    textTransform: 'none',
                                    fontSize: isMobile ? '0.65rem' : '0.875rem',
                                    padding: isMobile ? '4px 6px' : '8px 16px',
                                    minWidth: isMobile ? '70px' : 'auto',
                                    '&:hover': { bgcolor: '#e8f5e9' },
                                    ...styles.buttonHover
                                }}
                            >
                                Anterior
                            </Button>
                            <Button
                                startIcon={<ArrowForwardIcon />}
                                onClick={() => setCurrentStep(prev => Math.min(getTotalSteps(), prev + 1))}
                                disabled={currentStep === getTotalSteps()}
                                sx={{
                                    color: '#10641c',
                                    textTransform: 'none',
                                    fontSize: isMobile ? '0.65rem' : '0.875rem',
                                    padding: isMobile ? '4px 6px' : '8px 16px',
                                    minWidth: isMobile ? '70px' : 'auto',
                                    '&:hover': { bgcolor: '#e8f5e9' },
                                    ...styles.buttonHover
                                }}
                            >
                                Próximo
                            </Button>
                        </Box>
                    )}
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-end',
                        width: isMobile ? 'auto' : 'auto',
                    }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/city_hall/forms-templates')}
                            sx={{
                                fontSize: isMobile ? '0.65rem' : '0.875rem',
                                borderColor: '#10641c',
                                color: '#10641c',
                                padding: isMobile ? '6px 10px' : '8px 16px',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease-in-out',
                                minWidth: isMobile ? '70px' : 'auto',
                                '&:hover': {
                                    borderColor: '#0a3d0f',
                                    color: '#0a3d0f',
                                    backgroundColor: '#e8f5e9',
                                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
                                },
                            }}
                            variant="outlined"
                        >
                            Voltar
                        </Button>
                        <Button
                            onClick={() => navigate('/city_hall/forms-templates')}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                fontSize: isMobile ? '0.65rem' : '0.875rem',
                                padding: isMobile ? '6px 10px' : '8px 16px',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease-in-out',
                                minWidth: isMobile ? '70px' : 'auto',
                                '&:hover': {
                                    bgcolor: '#cc0000',
                                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
                                },
                                ...styles.buttonHover
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                fontSize: isMobile ? '0.65rem' : '0.875rem',
                                padding: isMobile ? '6px 10px' : '8px 16px',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease-in-out',
                                minWidth: isMobile ? '70px' : 'auto',
                                '&:hover': {
                                    bgcolor: '#0a3d0f',
                                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
                                },
                                ...styles.buttonHover
                            }}
                            disabled={!template?.id || !hasChanges}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                        bgcolor: snackbar.severity === 'success' ? '#e8f5e9' : '#ffebee',
                        color: snackbar.severity === 'success' ? '#10641c' : '#d32f2f'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FormBuilderPage;